/**
 * ContractorDocsPreview · iter303ak-fix28 — Work Booked · T70 · Action 10
 * ──────────────────────────────────────────────────────────────────────
 * Per accepted child × contractor: preview 3 docs stacked vertically
 *   1. Work Sheet (per-child scope)  · /api/reports/work-sheet/{child_id}
 *   2. Contractor Sheet (per contractor) · /api/reports/contractor-sheet/{child_id}?contractor_id=…
 *   3. Office Contractor Sheet (all trades) · /api/reports/contractor-detailed/{child_id}
 *
 * Per-contractor "Include in send" toggle (default on). Selection flows
 * into ContractorDocsSendBundle.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle2, FileText, ExternalLink, User, ChevronDown, ChevronUp } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

const DOC_KEYS = [
  { key: 'work-sheet', label: 'Work Sheet' },
  { key: 'contractor-sheet', label: 'Contractor Sheet (labour contract)' },
  { key: 'contractor-detailed', label: 'Office Contractor Sheet' },
];

function pdfUrl(childId, docKey, contractorId) {
  const base = `${BACKEND}/api/reports/${docKey}/${childId}?inline=1`;
  const withCid = contractorId ? `${base}&contractor_id=${contractorId}` : base;
  return withCid;
}

export default function ContractorDocsPreview({ action, capturedData, onNext, onBack, onCancel, canBack, value, onChange }) {
  const params = action.params || {};

  const acceptedChildren = useMemo(() => {
    const decisions = capturedData?.appraisal_record?.child_decisions
      || capturedData?.appraisal?.child_decisions
      || {};
    return Object.entries(decisions)
      .filter(([, d]) => d?.decision === 'accepted')
      .map(([child_id, d]) => ({ child_id, ...d }));
  }, [capturedData]);

  const [items, setItems] = useState([]);      // { child_id, appraisal_no, quote_type_ref, contractor_id, contractor_name }
  const [include, setInclude] = useState(() => value?.include || {}); // key -> bool
  const [expanded, setExpanded] = useState(() => value?.expanded || {}); // key -> bool
  const [loading, setLoading] = useState(true);

  const loadContractors = useCallback(async () => {
    if (!acceptedChildren.length) { setLoading(false); return; }
    setLoading(true);
    const list = [];
    const nextInclude = { ...include };
    const nextExpanded = { ...expanded };
    let firstKey = null;
    for (const c of acceptedChildren) {
      try {
        const { data: overview } = await api.get(`/appraisals/${c.child_id}/contractor-overview`);
        const assignments = overview?.type_assignments || {};
        const seen = new Set();
        for (const cid of Object.values(assignments)) {
          if (!cid || seen.has(cid)) continue;
          seen.add(cid);
          let contractor_name = '';
          try {
            const { data: cdoc } = await api.get(`/contractors/${cid}`);
            contractor_name = cdoc?.contractor_name || cdoc?.company_name || '';
          } catch { /* silent */ }
          const key = `${c.child_id}|${cid}`;
          if (!(key in nextInclude)) nextInclude[key] = true;
          if (!firstKey) firstKey = key;
          list.push({
            child_id: c.child_id,
            appraisal_no: c.appraisal_no,
            quote_type_ref: c.quote_type_ref,
            variant_label: c.variant_label,
            contractor_id: cid,
            contractor_name,
          });
        }
      } catch { /* silent */ }
    }
    // Auto-expand the first row so operator sees a preview immediately.
    if (firstKey && !(firstKey in nextExpanded)) nextExpanded[firstKey] = true;
    setItems(list);
    setInclude(nextInclude);
    setExpanded(nextExpanded);
    setLoading(false);
  }, [acceptedChildren]);   

  useEffect(() => { loadContractors(); }, [loadContractors]);

  useEffect(() => {
    if (onChange) onChange({ include, expanded, items });
  }, [include, expanded, items]);   

  const toggleInclude = (key) => setInclude(prev => ({ ...prev, [key]: !prev[key] }));
  const toggleExpanded = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const includedCount = Object.values(include).filter(Boolean).length;
  const canContinue = items.length > 0 && includedCount > 0;

  return (
    <div className="space-y-4" data-testid="wf-contractor-docs-preview">
      <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 mt-0.5 shrink-0 text-indigo-700" />
          <div className="flex-1">
            <div className="text-sm font-semibold text-indigo-900">Preview Contractor Docs Bundle</div>
            <div className="text-sm mt-1 text-indigo-800">
              {params.prompt || 'Preview all 3 docs per contractor: Work Sheet + Contractor Sheet + Office Contractor Sheet. Toggle inclusion per row.'}
            </div>
          </div>
        </div>
      </div>

      {loading && <div className="text-sm text-slate-500 italic p-3">Loading contractors…</div>}

      {!loading && items.length === 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
          No contractors assigned. Complete Accepted T30 (Contractor Report Raised) first.
        </div>
      )}

      {items.length > 0 && (
        <div className="space-y-3">
          {items.map(it => {
            const key = `${it.child_id}|${it.contractor_id}`;
            const label = `${it.quote_type_ref || ''}${it.variant_label ? ` · ${it.variant_label}` : ''} — ${it.contractor_name || it.contractor_id}`;
            const isOpen = !!expanded[key];
            const isIncluded = !!include[key];
            return (
              <div key={key} className={`border rounded-lg overflow-hidden ${isIncluded ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-200 opacity-70'}`}
                data-testid={`wf-cdp-row-${it.child_id}-${it.contractor_id}`}>
                <div className="flex items-center gap-3 p-3 bg-slate-50 border-b border-slate-200 flex-wrap">
                  <button onClick={() => toggleExpanded(key)}
                    data-testid={`wf-cdp-toggle-${it.child_id}-${it.contractor_id}`}
                    className="flex items-center gap-1 text-slate-600 hover:text-slate-900">
                    {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-slate-800 truncate">{label}</span>
                      {it.appraisal_no && <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-800 bg-indigo-100 border border-indigo-300 rounded px-1.5 py-0.5">A{it.appraisal_no}</span>}
                    </div>
                    {it.contractor_name && (
                      <div className="text-[11px] text-slate-600 mt-0.5 flex items-center gap-1">
                        <User size={11} /> {it.contractor_name}
                      </div>
                    )}
                  </div>
                  <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
                    <input type="checkbox" checked={isIncluded} onChange={() => toggleInclude(key)}
                      data-testid={`wf-cdp-include-${it.child_id}-${it.contractor_id}`}
                      className="w-4 h-4" />
                    Include in send
                  </label>
                </div>
                {isOpen && (
                  <div className="p-3 space-y-3">
                    {DOC_KEYS.map(doc => (
                      <div key={doc.key} className="border border-slate-200 rounded overflow-hidden">
                        <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
                          <div className="text-xs font-semibold text-slate-700">{doc.label}</div>
                          <a href={pdfUrl(it.child_id, doc.key, it.contractor_id)} target="_blank" rel="noopener noreferrer"
                            data-testid={`wf-cdp-open-${it.child_id}-${it.contractor_id}-${doc.key}`}
                            className="text-[11px] text-sky-600 hover:text-sky-800 inline-flex items-center gap-1">
                            <ExternalLink size={11} /> Open
                          </a>
                        </div>
                        <iframe src={pdfUrl(it.child_id, doc.key, it.contractor_id)}
                          title={`${doc.label} · ${label}`}
                          className="w-full border-0 bg-slate-100"
                          style={{ height: 500 }}
                          data-testid={`wf-cdp-iframe-${it.child_id}-${it.contractor_id}-${doc.key}`} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 pt-2">
        {canBack ? <button onClick={onBack} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">← Back</button> : <span />}
        <div className="flex gap-2 items-center">
          <div className="text-xs text-slate-600 mr-2">
            <b className="text-slate-800">{includedCount}</b> of <b className="text-slate-800">{items.length}</b> included
          </div>
          {onCancel && <button onClick={onCancel} className="px-3 py-1.5 rounded-md text-sm text-slate-600 hover:bg-slate-100">Cancel</button>}
          <button onClick={onNext} disabled={!canContinue}
            data-testid="wf-cdp-continue"
            className="px-4 py-1.5 rounded-md text-sm bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 inline-flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Continue
          </button>
        </div>
      </div>
    </div>
  );
}
